/**
 * Site URL resolution.
 *
 * Returns the canonical URL the site is served from in the current environment:
 * 1. NEXT_PUBLIC_SITE_URL (explicit override, set per Vercel environment)
 * 2. https://aheadhealth.com on production Vercel deploys — never the ephemeral
 *    VERCEL_URL, which would poison canonical / og:url / hreflang with a
 *    per-deploy hostname like marketing-site-<hash>.vercel.app.
 * 3. https://{VERCEL_URL} on previews / branch deploys
 * 4. http://localhost:3001 only in `next dev`
 * 5. https://aheadhealth.com everywhere else (CI builds, anything without env hints)
 *
 * Set NEXT_PUBLIC_SITE_URL=https://new.aheadhealth.dev on the preview environment
 * for stable preview URLs that don't change every deploy.
 */
const PRODUCTION_URL = "https://www.aheadhealth.com"

function rawSiteUrl(): string {
  if (process.env.NEXT_PUBLIC_SITE_URL) return process.env.NEXT_PUBLIC_SITE_URL
  if (process.env.VERCEL_ENV === "production") return PRODUCTION_URL
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`
  if (process.env.NODE_ENV === "development") return "http://localhost:3001"
  return PRODUCTION_URL
}

export const SITE_URL = rawSiteUrl().replace(/\/$/, "")

/**
 * Resolve a path or URL to an absolute URL using SITE_URL.
 * - Already-absolute URLs (http/https) are returned unchanged — this guards against
 *   accidentally producing `https://aheadhealth.comhttps://cms.aheadhealth.dev/...`.
 * - Protocol-relative URLs (`//host/path`) are returned unchanged.
 * - Relative paths get the SITE_URL prefix; missing leading slash is added.
 */
export function absoluteUrl(pathOrUrl: string | undefined | null): string {
  if (!pathOrUrl) return SITE_URL
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
  if (pathOrUrl.startsWith("//")) return `https:${pathOrUrl}`
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`
  return `${SITE_URL}${path}`
}
